'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { Tooltip } from '@repo/design-system/components/ds/tooltip/tooltip';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { DropdownMenu, DropdownMenuItem } from '@repo/design-system/components/ui/dropdown-menu';
import { Type } from '@repo/design-system/components/ui/type';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { CatalogGameMapFlags } from '@repo/types/catalog';
import { hasFlag } from '@repo/utility/flags';
import React from 'react';
import { useMapSwitcher } from './hooks/useMapSwitcher';
export interface MapSelectorProps {
  onMapChange?: (mapId: string) => void;
  titleSlug?: string; // Optional for URL updates
  updateUrl?: boolean; // Whether to update the URL when switching maps
  isCollapsed?: boolean; // Whether the sidebar is collapsed
}
const mapThumbnail = (mapId: string, thumbnailId: string) => {
  return `${process.env.NEXT_PUBLIC_CDN_URL}/game_map_thumbnails/${mapId}/${thumbnailId}.webp`;
};
export const MapSelector: React.FC<MapSelectorProps> = t0 => {
  const $ = _c(20);
  const {
    isCollapsed,
    onMapChange
  } = t0;
  const {
    isSwitching,
    availableMaps,
    currentMapExternalId,
    hasMultipleMaps,
    isError,
    switchMap,
    pendingExternalId
  } = useMapSwitcher();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["common", "game_map"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useT(t1);
  const effectiveSelectedId = pendingExternalId || currentMapExternalId;
  let t2;
  if ($[1] !== availableMaps || $[2] !== effectiveSelectedId) {
    t2 = availableMaps?.find(map => map.externalId === effectiveSelectedId);
    $[1] = availableMaps;
    $[2] = effectiveSelectedId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const selectedMap = t2;
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  let t3;
  let t4;
  if ($[4] !== dropdownOpen || $[5] !== isCollapsed) {
    t3 = () => {
      if (isCollapsed && dropdownOpen) {
        setDropdownOpen(false);
      }
    };
    t4 = [isCollapsed, dropdownOpen];
    $[4] = dropdownOpen;
    $[5] = isCollapsed;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  React.useEffect(t3, t4);
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = css({
      width: "[60px]",
      height: "[36px]",
      objectFit: "cover",
      borderRadius: "6",
      borderWidth: "[.5px]",
      borderStyle: "solid",
      borderColor: "whiteAlpha.10"
    });
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const thumbnailStyles = t5;
  let t6;
  if ($[9] !== availableMaps || $[10] !== dropdownOpen || $[11] !== effectiveSelectedId || $[12] !== hasMultipleMaps || $[13] !== isError || $[14] !== isSwitching || $[15] !== onMapChange || $[16] !== selectedMap || $[17] !== switchMap || $[18] !== t) {
    t6 = hasMultipleMaps ? <DropdownMenu open={dropdownOpen} onOpenChange={details => setDropdownOpen(details.open)} trigger={<Button color="secondary" size="lg" fullWidth={true} disabled={isSwitching} className={css({
      borderRadius: "16"
    })}><Icon icon="map" decorative={true} /><FlexColumn width="full"><Type color="whiteAlpha.80" variant="caption">{isSwitching ? t(_temp, {
            ns: "common"
          }) : isError ? t(_temp2, {
            ns: "common"
          }) : t(_temp3, {
            ns: "game_map"
          })}</Type>{selectedMap && <Type variant="header-xs" textAlign="left">{selectedMap.name}</Type>}</FlexColumn><Icon icon="arrow_drop_down" decorative={true} /></Button>} size="lg" placement="bottom-start" matchTriggerWidth={true} contentClassName={css({
      borderRadius: "16"
    })}>{availableMaps?.map(map_0 => <DropdownMenuItem key={map_0.externalId} leadingIcon="check" leadingIconProps={{
        style: {
          visibility: map_0.externalId === effectiveSelectedId ? "visible" : "hidden"
        }
      }} disabled={isSwitching} value={map_0.externalId} onClick={() => {
        switchMap(map_0.externalId, {
          updatePath: true
        });
        onMapChange?.(map_0.externalId);
        setDropdownOpen(false);
      }}><FlexRow gap="8" align="center" width="full"><object className={thumbnailStyles} data={mapThumbnail(map_0.id, map_0.thumbnailId)} type="image/webp" /><Type variant="body-s" color="text.primary" truncate={true} className={css({
            flex: "1",
            minWidth: "0"
          })}>{map_0.name}</Type><FlexRow gap="4" align="center" className={css.raw({
            flexShrink: 0
          })}>{hasFlag(map_0.flags, CatalogGameMapFlags.SupportsLiveLocation) && <Tooltip content={t(_temp4, {
              ns: "game_map"
            })} placement="top" size="sm" zIndex={10000}><FlexRow align="center"><Icon icon="radar" size="sm" decorative={true} className={css({
                  color: "limeGreen.400"
                })} /></FlexRow></Tooltip>}{map_0.supportsTeleport && <Tooltip content={t(_temp5, {
              ns: "game_map"
            })} placement="top" size="sm" zIndex={10000}><FlexRow align="center"><Icon icon="sprint" size="sm" decorative={true} className={css({
                  color: "cyan.400"
                })} /></FlexRow></Tooltip>}</FlexRow></FlexRow></DropdownMenuItem>)}</DropdownMenu> : null;
    $[9] = availableMaps;
    $[10] = dropdownOpen;
    $[11] = effectiveSelectedId;
    $[12] = hasMultipleMaps;
    $[13] = isError;
    $[14] = isSwitching;
    $[15] = onMapChange;
    $[16] = selectedMap;
    $[17] = switchMap;
    $[18] = t;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
};
function _temp($) {
  return $.loading;
}
function _temp2($_0) {
  return $_0.error;
}
function _temp3($_1) {
  return $_1.choose_map;
}
function _temp4($_2) {
  return $_2.maps_root.live_location_supported;
}
function _temp5($_3) {
  return $_3.maps_root.teleport_supported;
}